// Creator analytics (accounts wave 6: GET /v1/creators/me/games/:id/analytics?days=): reading the answer
// defensively and shaping it for the charts. Privacy: days with fewer than `minPlayers` players come back with every
// number null and `suppressed: true`; they're shown as gaps with a note, never as zero. Retention rates are also null
// when the cohort is small or the day hasn't come yet, which are different things and are told apart here.
// Dates are UTC days ("2026-10-16") and are formatted in UTC so they never shift a day in the viewer's zone.

export const RANGES = [7, 30, 90] as const;
export type Range = (typeof RANGES)[number];

export interface DayRow {
  date: string;
  players: number | null;
  newPlayers: number | null;
  sessions: number | null;
  activeSeconds: number | null;
  medianSessionSeconds: number | null;
  d1Rate: number | null;
  d7Rate: number | null;
  suppressed: boolean;
}

export interface Retention {
  cohort: number | null;
  returned: number | null;
  rate: number | null;
}

export interface Bucket {
  label: string;
  minSeconds: number;
  maxSeconds: number | null;
  players: number | null;
}

export interface Analytics {
  gameId: string;
  minPlayers: number;
  /** a gap this long (seconds) without play ends a session */
  sessionGapSeconds: number | null;
  days: DayRow[];
  totals: { players: number | null; newPlayers: number | null; sessions: number | null; activeSeconds: number | null; medianSessionSeconds: number | null; suppressed: boolean };
  retention: { d1: Retention | null; d7: Retention | null };
  histogram: { suppressed: boolean; buckets: Bucket[] };
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
/** a count or seconds: a finite number ≥ 0, else null */
const n = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null);
/** a rate in 0..1 (a percentage over 1 is read as a percentage) */
const rate = (v: unknown): number | null => {
  const x = n(v);
  if (x === null) return null;
  return x > 1 ? Math.min(1, x / 100) : x;
};

export function clampRange(v: unknown): Range {
  const x = Number(v);
  return (RANGES as readonly number[]).includes(x) ? (x as Range) : 30;
}

function retention(v: unknown): Retention | null {
  if (!v || typeof v !== "object") return null;
  const r = obj(v);
  return { cohort: n(r.cohort), returned: n(r.returned), rate: rate(r.rate) };
}

export function parseAnalytics(raw: unknown, gameId: string): Analytics {
  const r = obj(raw);
  const minPlayers = n(r.minPlayers) ?? 5;
  const days: DayRow[] = (Array.isArray(r.days) ? r.days : [])
    .map(obj)
    .filter((d) => typeof d.date === "string" && DATE.test(d.date))
    .map((d) => {
      const suppressed = d.suppressed === true;
      const v = (x: unknown) => (suppressed ? null : n(x));
      const pr = (x: unknown) => (suppressed ? null : rate(x));
      return {
        date: d.date as string,
        players: v(d.players),
        newPlayers: v(d.newPlayers),
        sessions: v(d.sessions),
        activeSeconds: v(d.activeSeconds),
        medianSessionSeconds: v(d.medianSessionSeconds),
        d1Rate: pr(d.d1Rate),
        d7Rate: pr(d.d7Rate),
        suppressed,
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
  const t = obj(r.totals);
  const tSup = t.suppressed === true;
  const tv = (x: unknown) => (tSup ? null : n(x));
  const hist = obj(r.histogram);
  const buckets: Bucket[] = (Array.isArray(hist.buckets) ? hist.buckets : []).map(obj).flatMap((b) => {
    const min = n(b.minSeconds);
    if (min === null) return [];
    const max = n(b.maxSeconds);
    return [{ label: typeof b.label === "string" && b.label ? b.label : bucketLabel(min, max), minSeconds: min, maxSeconds: max, players: n(b.players) }];
  });
  const ret = obj(r.retention);
  return {
    gameId: typeof r.gameId === "string" ? r.gameId : gameId,
    minPlayers,
    sessionGapSeconds: n(r.sessionGapSeconds),
    days,
    totals: { players: tv(t.players), newPlayers: tv(t.newPlayers), sessions: tv(t.sessions), activeSeconds: tv(t.activeSeconds), medianSessionSeconds: tv(t.medianSessionSeconds), suppressed: tSup },
    retention: { d1: retention(ret.d1), d7: retention(ret.d7) },
    histogram: { suppressed: hist.suppressed === true, buckets },
  };
}

/** "0–5 min", "1–2 h", "2 h+" for a bucket without a label. */
export function bucketLabel(min: number, max: number | null): string {
  const t = (s: number) => (s < 3600 ? `${Math.round(s / 60)} min` : `${+(s / 3600).toFixed(1)} h`);
  return max === null ? `${t(min)}+` : `${t(min)}–${t(max)}`;
}

// ---- days on the axis ------------------------------------------------------------------------------------------

const DAY_MS = 86_400_000;
const dayNum = (date: string) => Math.round(Date.parse(`${date}T00:00:00Z`) / DAY_MS);
const dateOf = (num: number) => new Date(num * DAY_MS).toISOString().slice(0, 10);
export const todayUtc = (now = Date.now()) => new Date(now).toISOString().slice(0, 10);

/**
 * Every day from the first to the last in the answer, in order, so a day the answer leaves out still has its place
 * on the axis (and shows as a gap) instead of shifting the others.
 */
export function fillDays(days: DayRow[]): (DayRow | { date: string; missing: true })[] {
  if (!days.length) return [];
  const by = new Map(days.map((d) => [d.date, d]));
  const first = dayNum(days[0]!.date);
  const last = dayNum(days.at(-1)!.date);
  const out: (DayRow | { date: string; missing: true })[] = [];
  for (let i = first; i <= last && out.length < 400; i++) {
    const date = dateOf(i);
    out.push(by.get(date) ?? { date, missing: true });
  }
  return out;
}

/** Why a point has no value: hidden for privacy, not measurable yet, a cohort too small, or no data for the day. */
export type Gap = "suppressed" | "pending" | "small" | "missing";

export interface Point {
  date: string;
  value: number | null;
  gap: Gap | null;
}

export type DayMetric = "players" | "newPlayers" | "sessions" | "activeSeconds" | "medianSessionSeconds";

/** One number per day, with nulls explained. */
export function dayPoints(days: DayRow[], metric: DayMetric): Point[] {
  return fillDays(days).map((d) => {
    if ("missing" in d) return { date: d.date, value: null, gap: "missing" };
    if (d.suppressed) return { date: d.date, value: null, gap: "suppressed" };
    const value = d[metric];
    return { date: d.date, value, gap: value === null ? "missing" : null };
  });
}

/**
 * Day-1 or day-7 return of each day's new players. A null rate on a day that wasn't suppressed means either too few
 * new players that day to show a share, or the day it measures hasn't come (or finished) yet: the last 1 or 7 days.
 */
export function retentionPoints(days: DayRow[], which: "d1" | "d7", now = Date.now(), minPlayers = 5): Point[] {
  const lag = which === "d1" ? 1 : 7;
  const today = dayNum(todayUtc(now));
  return fillDays(days).map((d) => {
    if ("missing" in d) return { date: d.date, value: null, gap: "missing" };
    if (d.suppressed) return { date: d.date, value: null, gap: "suppressed" };
    const value = which === "d1" ? d.d1Rate : d.d7Rate;
    if (value !== null) return { date: d.date, value, gap: null };
    // a cohort we know is small says so; otherwise a day whose return day hasn't finished is "not yet"
    if (d.newPlayers !== null && d.newPlayers < minPlayers) return { date: d.date, value: null, gap: "small" };
    if (dayNum(d.date) + lag >= today) return { date: d.date, value: null, gap: "pending" };
    return { date: d.date, value: null, gap: d.newPlayers === null ? "small" : "missing" };
  });
}

/** Runs of consecutive values, for drawing lines that break at every gap (never bridged). */
export function segments(points: Point[]): { i: number; value: number }[][] {
  const out: { i: number; value: number }[][] = [];
  let run: { i: number; value: number }[] = [];
  points.forEach((p, i) => {
    if (p.value === null) {
      if (run.length) out.push(run);
      run = [];
    } else run.push({ i, value: p.value });
  });
  if (run.length) out.push(run);
  return out;
}

export function gapCounts(points: Point[]): Record<Gap, number> {
  const c: Record<Gap, number> = { suppressed: 0, pending: 0, small: 0, missing: 0 };
  for (const p of points) if (p.gap) c[p.gap]++;
  return c;
}

/** Words for a gap, used in tooltips and the table view. */
export function gapText(gap: Gap, minPlayers: number): string {
  return {
    suppressed: `fewer than ${minPlayers} players`,
    pending: "not measurable yet",
    small: `fewer than ${minPlayers} new players`,
    missing: "no data",
  }[gap];
}

/** Clean axis ticks from 0 to at least `max`: 0, step, 2·step… with a 1/2/2.5/5 × 10ⁿ step. */
export function niceTicks(max: number, target = 4): number[] {
  if (!(max > 0)) return [0, 1];
  const raw = max / target;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow;
  const ticks: number[] = [];
  for (let v = 0; v < max + step * 0.999; v += step) ticks.push(+v.toFixed(10));
  if (ticks.length < 2) ticks.push(step);
  return ticks;
}

/** "16 Oct" for a UTC day. */
export function fmtDay(date: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" }).format(Date.parse(`${date}T00:00:00Z`));
}

/** 1284 → "1,284"; 12_900 → "12.9K". */
export function compact(v: number): string {
  if (Math.abs(v) >= 10_000) return `${+(v / 1000).toFixed(1)}K`;
  return Math.round(v).toLocaleString("en");
}

export const pct = (r: number) => `${Math.round(r * 100)}%`;
